import { Component, ElementRef, input, output, signal, viewChildren } from '@angular/core';

export const TOTP_DIGIT_COUNT = 6;

@Component({
  selector: 'avs-totp-code',
  styleUrl: './totp-code.component.scss',
  templateUrl: './totp-code.component.html',
})
export class TotpCodeComponent {
  invalid = input(false);
  useRecoveryCode = input(false);
  valueChange = output<string>();

  protected digits = signal<string[]>(new Array(TOTP_DIGIT_COUNT).fill(''));
  protected recoveryCode = signal('');

  private digitInputs = viewChildren<ElementRef<HTMLInputElement>>('digitInput');

  reset() {
    this.digits.set(new Array(TOTP_DIGIT_COUNT).fill(''));
    this.recoveryCode.set('');
    this.digitInputs()[0]?.nativeElement.focus();
    this.emitValue();
  }

  protected onDigitFocus(event: FocusEvent) {
    (event.target as HTMLInputElement).select();
  }

  protected onDigitInput(index: number, event: Event) {
    const value = (event.target as HTMLInputElement).value.replace(/\D/g, '').slice(-1);

    this.digits.update(digits => digits.map((digit, i) => (i === index ? value : digit)));

    if (value && index < TOTP_DIGIT_COUNT - 1) {
      this.digitInputs()[index + 1]?.nativeElement.focus();
    }

    this.emitValue();
  }

  protected onDigitKeydown(index: number, event: KeyboardEvent) {
    if (event.key === 'Backspace' && !this.digits()[index] && index > 0) {
      this.digitInputs()[index - 1]?.nativeElement.focus();
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      this.digitInputs()[index - 1]?.nativeElement.focus();
    }

    if (event.key === 'ArrowRight' && index < TOTP_DIGIT_COUNT - 1) {
      this.digitInputs()[index + 1]?.nativeElement.focus();
    }
  }

  protected onDigitPaste(event: ClipboardEvent) {
    event.preventDefault();

    const pasted = event.clipboardData?.getData('text').replace(/\D/g, '').slice(0, TOTP_DIGIT_COUNT) ?? '';

    this.digits.set(new Array(TOTP_DIGIT_COUNT).fill('').map((_, i) => pasted[i] ?? ''));

    const focusIndex = Math.min(pasted.length, TOTP_DIGIT_COUNT - 1);

    this.digitInputs()[focusIndex]?.nativeElement.focus();

    this.emitValue();
  }

  protected onRecoveryCodeInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const raw = input.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
    const formatted = raw.length > 4 ? `${raw.slice(0, 4)}-${raw.slice(4)}` : raw;

    input.value = formatted;
    this.recoveryCode.set(formatted);
    this.emitValue();
  }

  private emitValue() {
    this.valueChange.emit(this.useRecoveryCode() ? this.recoveryCode() : this.digits().join(''));
  }
}
